import { useState, type ComponentType } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CallIcon, CheckIcon, ChevronIcon, CircleCheckIcon, CloseIcon, Drawer, EditIcon, FlowsIcon, InstagramIcon, LinkIcon, PlanIcon, PlusIcon, RedditIcon, relativeTime, Segments, Select, ShieldIcon, TelegramIcon, TrashIcon, useConfirm, useToast, XIcon } from "@signet/ui";
import { api, compact, CreatorAvatar, NowPanelHead, useCreatorPhotos, usePersonName, useSortable } from "@signet/web-kit";
import { CLink, creatorHref } from "./shared.tsx";
import { brandColour, PlatformMark } from "./brand-marks.tsx";
import { parseAccountUrl, PLATFORM_ORDER, PLATFORMS, type PlatformKey } from "../shared/platforms.ts";
import { isComplete, progressOf, type StepDef, type Tracker, type TrackerCreator, type TrackerStep } from "../shared/onboarding-tracker.ts";
import { DefaultsEditor } from "./onboarding-defaults.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Ben's onboarding tracker on Now (his whiteboard and mockup, 2026-11-02):
   new creators down the side, his steps across in his order, one tick
   each, a progress bar. All, In progress and Complete, so a finished creator
   can still be looked back on. A creator's name opens the side panel: who
   ticked what and when, the plan, notes, and their main IG accounts, which
   go into Main Accounts. Every tick is by hand (Jay, 2026-11-04). Creators
   drag into any order, and "Edit defaults" changes the steps and the lines
   a new creator starts with (Jay, 2026-11-04).
   ────────────────────────────────────────────────────────────────────────── */

export const BASE = "/api/m/creators/onboarding-tracker";
export const KEY = ["creators", "onboarding-tracker"] as const;

type Tab = "all" | "progress" | "complete";

const fetchTracker = () => api<Tracker>(BASE);

function useTrackerWrite() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (call: { path: string; method: string; body?: unknown }) => api<Tracker>(`${BASE}${call.path}`, { method: call.method, body: call.body }),
    onSuccess: (data) => client.setQueryData(KEY, data),
    onError: (error) => toast.error("Couldn't save", error instanceof Error ? error.message : undefined),
  });
}

const enc = encodeURIComponent;

export function OnboardingTrackerPanel() {
  const tracker = useQuery({ queryKey: KEY, queryFn: fetchTracker, refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const write = useTrackerWrite();
  const [tab, setTab] = useState<Tab>("progress");
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const client = useQueryClient();

  const steps = tracker.data?.steps ?? [];
  const all = tracker.data?.creators ?? [];
  const done = all.filter((c) => isComplete(c, steps));
  const going = all.filter((c) => !isComplete(c, steps));
  const rows = tab === "all" ? all : tab === "complete" ? done : going;
  // Rows drag into any order (Jay, 2026-11-04: "dragging RHEA above AYLA"). A tab shows some of
  // them, so the dragged order is put back into the full list in the slots those rows held.
  const sort = useSortable({
    ids: rows.map((c) => c.creator),
    groupOf: () => "rows",
    prefix: "cr-ot",
    ignore: "button:not(.cr-ot-name), a, input, select, textarea",
    onMove: (_id, _place, order) => {
      const shown = new Set(order);
      const queue = [...order];
      const full = all.map((c) => (shown.has(c.creator) ? queue.shift()! : c.creator));
      client.setQueryData<Tracker>(KEY, (old) => old && { ...old, creators: full.map((n) => old.creators.find((c) => c.creator === n)!).filter(Boolean) });
      write.mutate({ path: "/order", method: "POST", body: { creators: full } });
    },
  });
  const opened = all.find((c) => c.creator === open) ?? null;

  const tick = (creator: string, step: TrackerStep, on: boolean) => write.mutate({ path: `/${enc(creator)}/ticks/${step}`, method: "PUT", body: { done: on } });

  return (
    <section className="panel cr-ot">
      <NowPanelHead
        label="Onboarding"
        title="New creators"
        sub={tracker.data ? `${going.length} in progress, ${done.length} complete` : " "}
        side={
          <span className="cr-ot-head-side">
            <Segments
              label="Which creators"
              value={tab}
              options={[
                { value: "all", label: `All (${all.length})` },
                { value: "progress", label: `In progress (${going.length})` },
                { value: "complete", label: `Complete (${done.length})` },
              ]}
              onChange={(next) => setTab(next as Tab)}
            />
            <button type="button" className="secondary-button compact" onClick={() => setEditing(true)}>
              <EditIcon size={12} /> Edit defaults
            </button>
            {tracker.data?.available.length ? (
              <span className="cr-ot-add-creator">
              <Select
                value=""
                compact
                searchable
                label="Add a creator"
                placeholder="Add a creator"
                options={tracker.data.available.map((name) => ({ value: name, label: name, image: photos(name) ?? undefined }))}
                onChange={(name) => name && write.mutate({ path: "", method: "POST", body: { creator: name } })}
              />
              </span>
            ) : null}
          </span>
        }
      />
      <div className="panel-body flush">
        {tracker.isError ? <p className="faint cr-ot-empty">Couldn't load the onboarding tracker.</p> : null}
        {tracker.data && !rows.length ? (
          <p className="cr-ot-empty">
            <CheckIcon size={15} /> {tab === "complete" ? "Nobody has finished onboarding yet." : tab === "progress" ? "Nobody is being onboarded right now." : "No creators on the tracker."}
          </p>
        ) : null}
        {rows.length ? (
          <div className="table-scroll">
            {/* Fixed layout so every step column is the same width, not as wide as its heading (Jay, 2026-11-04). */}
            <table className="cr-ot-table" style={{ ["--cr-ot-steps" as string]: steps.length }}>
              <colgroup>
                <col className="cr-ot-who-col" />
                {steps.map((s) => (
                  <col key={s.id} />
                ))}
                <col className="cr-ot-progress-colw" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="cr-ot-who">Creator</th>
                  {steps.map((s) => (
                    <th scope="col" key={s.id}>
                      <span className="cr-ot-colhead">
                        <StepIcon icon={s.icon} size={13} />
                        {s.label}
                      </span>
                    </th>
                  ))}
                  <th scope="col" className="cr-ot-progress-col">Progress</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  // Anywhere on the line opens the creator (Ben, 2026-11-04); the ticks keep their own click.
                  <tr key={c.creator} className={`cr-ot-row${open === c.creator ? " selected" : ""}`} {...sort.rowProps(c.creator)} onClick={() => !sort.consumeClick() && setOpen(c.creator)}>
                    <th scope="row" className="cr-ot-who">
                      <button
                        type="button"
                        className="cr-ot-name"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!sort.consumeClick()) setOpen(c.creator);
                        }}
                        aria-label={`Open ${c.creator}`}
                      >
                        <CreatorAvatar name={c.creator} src={photos(c.creator)} size={30} />
                        <span>{c.creator}</span>
                      </button>
                    </th>
                    {steps.map((s) => {
                      const on = Boolean(c.ticks[s.id]);
                      return (
                        <td key={s.id}>
                          <button
                            type="button"
                            className={`cr-ot-tick${on ? " on" : ""}`}
                            aria-pressed={on}
                            aria-label={`${s.label} for ${c.creator}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              tick(c.creator, s.id, !on);
                            }}
                          >
                            {on ? <CheckIcon size={14} /> : null}
                          </button>
                        </td>
                      );
                    })}
                    <td className="cr-ot-progress-col">
                      <Progress value={progressOf(c, steps)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      {opened ? <TrackerDrawer creator={opened} steps={steps} photo={photos(opened.creator)} onClose={() => setOpen(null)} onTick={tick} /> : null}
      {editing ? <DefaultsEditor onClose={() => setEditing(false)} /> : null}
    </section>
  );
}

function Progress({ value }: { value: number }) {
  return (
    <span className="cr-ot-progress">
      <span className="cr-ot-bar" aria-hidden="true">
        <span style={{ width: `${value}%` }} />
      </span>
      <span className="tabular">{value}%</span>
    </span>
  );
}

/** By the step's icon name; a step added in "Edit defaults" has none and gets a plain tick. */
const STEP_ICONS: Record<string, ComponentType<{ size?: number }>> = {
  telegram: TelegramIcon,
  links: LinkIcon,
  call: CallIcon,
  plan: PlanIcon,
  flows: FlowsIcon,
  ig: InstagramIcon,
  twitter: XIcon,
  reddit: RedditIcon,
  leaks: ShieldIcon,
};

export function StepIcon({ icon, size = 15 }: { icon: string | null; size?: number }) {
  const Icon = (icon && STEP_ICONS[icon]) || CircleCheckIcon;
  return <Icon size={size} />;
}

function TrackerDrawer({ creator, steps, photo, onClose, onTick }: { creator: TrackerCreator; steps: StepDef[]; photo: string | null; onClose: () => void; onTick: (creator: string, step: TrackerStep, on: boolean) => void }) {
  const write = useTrackerWrite();
  const { confirm, element } = useConfirm();
  const ticked = steps.filter((s) => creator.ticks[s.id]).length;
  const complete = isComplete(creator, steps);
  const next = steps.find((s) => !creator.ticks[s.id]);
  // Every step starts closed (Jay, 2026-11-04); the first unfinished one used to open by itself.
  const [open, setOpen] = useState<Set<TrackerStep>>(() => new Set());
  const toggle = (step: TrackerStep) =>
    setOpen((current) => {
      const nextSet = new Set(current);
      if (nextSet.has(step)) nextSet.delete(step);
      else nextSet.add(step);
      return nextSet;
    });

  const remove = async () => {
    const ok = await confirm({
      title: `Take ${creator.creator} off the tracker?`,
      body: "Their ticks, plan and notes are kept, and come back if they are added again.",
      confirmLabel: "Take off",
      danger: true,
    });
    if (!ok) return;
    write.mutate({ path: `/${enc(creator.creator)}`, method: "DELETE" }, { onSuccess: onClose });
  };

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={
        <span className="cr-ot-drawer-title">
          <CreatorAvatar name={creator.creator} src={photo} size={28} />
          {creator.creator}
        </span>
      }
      subtitle={`On the tracker since ${new Date(creator.addedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
      head={<span className={`cr-ot-status${complete ? " done" : ""}`}>{complete ? "Complete" : "In progress"}</span>}
      footer={
        <>
          <button type="button" className="text-button tiny danger cr-ot-remove" onClick={() => void remove()}>
            <TrashIcon size={13} /> Take off the tracker
          </button>
          <span className="spacer" />
          <CLink to={creatorHref(creator.creator)} className="text-button tiny accent">
            Open creator page
          </CLink>
        </>
      }
    >
      {element}
      <div className="cr-ot-drawer">
        <section className="cr-ot-summary" aria-label="Progress">
          <div className="cr-ot-summary-top">
            <span className="cr-ot-summary-figure tabular">
              {ticked}
              <small>of {steps.length} steps</small>
            </span>
            <span className="cr-ot-next">{next ? <>Next: <strong>{next.label}</strong></> : "Every step is done"}</span>
          </div>
          <span className="cr-ot-segments" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, 1fr)` }}>
            {steps.map((s) => (
              <i key={s.id} className={creator.ticks[s.id] ? "on" : undefined} title={s.label} />
            ))}
          </span>
        </section>

        <ul className="cr-ot-steps" aria-label="Steps">
          {steps.map((s) => (
            <StepRow
              key={s.id}
              creator={creator}
              step={s.id}
              icon={s.icon}
              label={s.label}
              expanded={open.has(s.id)}
              onToggle={() => toggle(s.id)}
              onTick={(on) => onTick(creator.creator, s.id, on)}
            />
          ))}
        </ul>

        <Writing key={`plan-${creator.creator}`} creator={creator.creator} field="plan" label="Plan" value={creator.plan} placeholder="What we're doing with them: platforms, content, flows." />
        <Writing key={`notes-${creator.creator}`} creator={creator.creator} field="notes" label="Notes" value={creator.notes} placeholder="Anything else worth keeping." />

        <Accounts creator={creator} />
      </div>
    </Drawer>
  );
}

function StepRow({ creator, step, icon, label, expanded, onToggle, onTick }: { creator: TrackerCreator; step: TrackerStep; icon: string | null; label: string; expanded: boolean; onToggle: () => void; onTick: (on: boolean) => void }) {
  const name = usePersonName();
  const write = useTrackerWrite();
  const [draft, setDraft] = useState("");
  const tick = creator.ticks[step];
  const client = useQueryClient();
  const lines = creator.subitems.filter((x) => x.step === step);
  // Lines drag into any order within their step (Jay, 2026-11-04). The new order shows at once and is saved behind it.
  const sort = useSortable({
    ids: lines.map((x) => x.id),
    groupOf: () => step,
    prefix: "cr-ot",
    onMove: (_id, _place, order) => {
      client.setQueryData<Tracker>(KEY, (old) => old && {
        ...old,
        creators: old.creators.map((c) => c.creator !== creator.creator ? c : {
          ...c,
          subitems: [...c.subitems.filter((x) => x.step !== step), ...order.map((id) => c.subitems.find((x) => x.id === id)!).filter(Boolean)],
        }),
      });
      write.mutate({ path: `/${enc(creator.creator)}/subitems/order`, method: "POST", body: { step, ids: order } });
    },
  });
  const doneLines = lines.filter((x) => x.done).length;
  const panelId = `cr-ot-sub-${step}`;

  const add = () => {
    const text = draft.trim();
    if (!text) return;
    write.mutate({ path: `/${enc(creator.creator)}/subitems`, method: "POST", body: { step, label: text } }, { onSuccess: () => setDraft("") });
  };

  return (
    <li className={`cr-ot-steprow${tick ? " done" : ""}${expanded ? " open" : ""}`}>
      <div className="cr-ot-stephead">
        <button type="button" className={`cr-ot-tick${tick ? " on" : ""}`} aria-pressed={Boolean(tick)} aria-label={`${label} done`} onClick={() => onTick(!tick)}>
          {tick ? <CheckIcon size={13} /> : null}
        </button>
        <button type="button" className="cr-ot-stepbutton" aria-expanded={expanded} aria-controls={panelId} onClick={onToggle}>
          <span className="cr-ot-stepicon" aria-hidden="true">
            <StepIcon icon={icon} />
          </span>
          <span className="cr-ot-steplabel">{label}</span>
          {lines.length ? <span className="cr-ot-count tabular">{doneLines}/{lines.length}</span> : null}
          <span className="cr-ot-when">{tick ? `${name(tick.by)}, ${relativeTime(tick.at)}` : ""}</span>
          <ChevronIcon size={14} direction={expanded ? "up" : "down"} className="cr-ot-chevron" />
        </button>
      </div>
      <div className="cr-ot-subwrap" id={panelId} hidden={!expanded}>
        <ul className="cr-ot-subs">
          {lines.map((x) => (
            <li key={x.id} className={`cr-ot-subline${x.done ? " done" : ""}`} {...sort.rowProps(x.id)}>
              <button
                type="button"
                className={`cr-ot-box${x.done ? " on" : ""}`}
                aria-pressed={Boolean(x.done)}
                aria-label={x.label}
                onClick={() => write.mutate({ path: `/${enc(creator.creator)}/subitems/${enc(x.id)}`, method: "PUT", body: { done: !x.done } })}
              >
                {x.done ? <CheckIcon size={11} /> : null}
              </button>
              <span className="cr-ot-sublabel">{x.label}</span>
              {x.done ? <span className="cr-ot-subwhen">{name(x.done.by)}</span> : null}
              <button
                type="button"
                className="icon-button ghost small cr-ot-subremove"
                aria-label={`Remove "${x.label}"`}
                onClick={() => write.mutate({ path: `/${enc(creator.creator)}/subitems/${enc(x.id)}`, method: "DELETE" })}
              >
                <CloseIcon size={12} />
              </button>
            </li>
          ))}
          <li className="cr-ot-subadd">
            <PlusIcon size={13} aria-hidden="true" />
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="Add a line"
              aria-label={`Add a line under ${label}`}
              maxLength={200}
            />
          </li>
        </ul>
      </div>
    </li>
  );
}

function Writing({ creator, field, label, value, placeholder }: { creator: string; field: "plan" | "notes"; label: string; value: string; placeholder: string }) {
  const write = useTrackerWrite();
  const toast = useToast();
  const [text, setText] = useState(value);
  const [base, setBase] = useState(value);
  // A save from elsewhere replaces the box only while nothing unsaved is in it.
  if (value !== base) {
    if (text === base) setText(value);
    setBase(value);
  }
  const changed = text !== value;
  const save = () => write.mutate({ path: `/${enc(creator)}`, method: "PUT", body: { [field]: text } }, { onSuccess: () => toast.success("Saved", `${label} for ${creator}.`) });
  return (
    <section className="cr-ot-block">
      <h3 className="cr-ot-section-title">{label}</h3>
      <textarea
        className="cr-ot-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && changed) {
            e.preventDefault();
            save();
          }
        }}
        placeholder={placeholder}
        rows={field === "plan" ? 5 : 3}
        aria-label={`${label} for ${creator}`}
        maxLength={20_000}
      />
      {changed ? (
        <span className="cr-ot-save-row">
          <button type="button" className="secondary-button compact" onClick={() => setText(value)}>Undo</button>
          <button type="button" className="button brass compact" disabled={write.isPending} onClick={save}>Save</button>
        </span>
      ) : null}
    </section>
  );
}

const platformLabel = (key: string) => PLATFORMS.find((p) => p.key === key)?.label ?? "Other";

const MAIN_ACCOUNTS_SAID: Record<string, string> = {
  added: "Added to Main Accounts too, read daily from now.",
  banned: "Added to Main Accounts too, marked Banned: Instagram can't find it.",
  already: "Already in Main Accounts.",
  unavailable: "Couldn't reach Instagram, so it isn't in Main Accounts yet. Add it on the Main Accounts page.",
  invalid: "That handle couldn't go into Main Accounts.",
  "no-access": "You can't add to Main Accounts, so it isn't there.",
};

/**
 * Every account a creator has, any platform, as they send them on Telegram (Ben, 2026-11-04). Kept in
 * the creator's Socials, grouped by platform under its name and logo with the platform's colour down
 * the side, so two white logos (X, TikTok, Threads) still read apart. An Instagram one also goes into
 * Main Accounts (Jay, 2026-11-04).
 */
function Accounts({ creator }: { creator: TrackerCreator }) {
  const client = useQueryClient();
  const toast = useToast();
  const remove = useTrackerWrite();
  const [raw, setRaw] = useState("");
  const [platform, setPlatform] = useState("");
  const parsed = raw.trim() ? parseAccountUrl(raw, (platform || null) as PlatformKey | null) : null;
  const add = useMutation({
    mutationFn: () =>
      api<Tracker & { added: { platform: string; handle: string | null; mainAccounts: string | null } }>(`${BASE}/${enc(creator.creator)}/accounts`, {
        body: { url: raw, platform: platform || null },
      }),
    onSuccess: (data) => {
      client.setQueryData(KEY, data);
      void client.invalidateQueries({ queryKey: ["mainaccounts"] });
      setRaw("");
      setPlatform("");
      const who = data.added.handle ? `@${data.added.handle}` : "The account";
      toast.success(`${platformLabel(data.added.platform)} added`, data.added.mainAccounts ? `${who}. ${MAIN_ACCOUNTS_SAID[data.added.mainAccounts] ?? ""}` : `${who} is in ${creator.creator}'s socials.`);
    },
    onError: (error) => toast.error("Couldn't add it", error instanceof Error ? error.message : undefined),
  });

  const groups = PLATFORM_ORDER.map((key) => ({ key, accounts: creator.accounts.filter((a) => a.platform === key) })).filter((g) => g.accounts.length);

  return (
    <section className="cr-ot-block">
      <h3 className="cr-ot-section-title">
        Accounts {creator.accounts.length ? <span className="faint tabular">{creator.accounts.length}</span> : null}
      </h3>
      {groups.length ? (
        <div className="cr-ot-accounts">
          {groups.map((g) => (
            <div key={g.key} className="cr-ot-accgroup" style={{ ["--cr-ot-brand" as string]: brandColour(g.key) ?? "var(--line)" }}>
              <div className="cr-ot-accgroup-head">
                <PlatformMark platform={g.key} size={15} />
                <span>{platformLabel(g.key)}</span>
                <span className="cr-ot-acccount tabular">{g.accounts.length}</span>
              </div>
              <ul>
                {g.accounts.map((a) => (
                  <li key={`${a.id ?? "main"}-${a.url}`}>
                    <a href={a.url} target="_blank" rel="noreferrer noopener" className="cr-ot-acclink">
                      {a.handle ? `@${a.handle}` : a.url.replace(/^https?:\/\/(www\.)?/, "")}
                    </a>
                    {a.followers !== null ? <span className="cr-ot-accmeta tabular">{compact(a.followers)} followers</span> : null}
                    {a.inMainAccounts ? (
                      <span className="cr-ot-acctag" title="Main Accounts reads this account">
                        <InstagramIcon size={11} /> Main Accounts
                      </span>
                    ) : null}
                    {a.id ? (
                      <button
                        type="button"
                        className="icon-button ghost small cr-ot-subremove"
                        aria-label={`Remove ${a.handle ?? a.url}`}
                        title={a.platform === "instagram" ? "Removes it from socials. Main Accounts keeps it." : "Remove"}
                        onClick={() => remove.mutate({ path: `/${enc(creator.creator)}/accounts/${enc(a.id!)}`, method: "DELETE" })}
                      >
                        <CloseIcon size={12} />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <p className="faint cr-ot-none">No accounts yet. Paste each one they send.</p>
      )}
      <form
        className="cr-ot-add"
        onSubmit={(e) => {
          e.preventDefault();
          if (raw.trim()) add.mutate();
        }}
      >
        <span className="cr-ot-add-platform">
          <Select
            value={platform}
            compact
            searchable
            label="Platform"
            placeholder="Detect"
            clearable
            options={PLATFORMS.filter((p) => p.key !== "other").map((p) => ({ value: p.key, label: p.label, icon: <PlatformMark platform={p.key} size={14} /> }))}
            onChange={(next) => setPlatform(next ?? "")}
          />
        </span>
        <span className="cr-ot-add-field">
          {parsed ? <PlatformMark platform={parsed.platform} size={14} /> : <LinkIcon size={14} aria-hidden="true" />}
          <input value={raw} onChange={(e) => setRaw(e.target.value)} placeholder={platform ? `${platformLabel(platform)} handle or link` : "Paste a profile link"} aria-label={`Add an account for ${creator.creator}`} />
        </span>
        <button type="submit" className="secondary-button compact" disabled={!raw.trim() || add.isPending}>
          <PlusIcon size={13} /> Add
        </button>
      </form>
      {raw.trim() && !parsed ? <p className="faint cr-ot-hint">Pick the platform to add a bare handle.</p> : null}
    </section>
  );
}
